// IndexedDB read cache for previously loaded API responses / entities (localforage).
import localforage from "localforage-esm";
import _ from "lodash";

const forage = localforage.createInstance({
  name: "LionOffline",
  storeName: "entities",
});

const CURRENT_USER_KEY = "meta:current_user";
const LOOKUPS_SYNCED_AT_KEY = "meta:lookups_synced_at";
const URL_PREFIX = "url:";
const ENTITY_PREFIX = "entity:";
const SLICE_INDEX_PREFIX = "slice:";

const urlKey = (method, url) => `${URL_PREFIX}${_.toUpper(method || "GET")}:${url}`;
const entityKey = (slice, id) => `${ENTITY_PREFIX}${slice}:${id}`;
const sliceIndexKey = (slice) => `${SLICE_INDEX_PREFIX}${slice}`;

// /auth/me?page= tracks navigation — same user payload, one cache key
export const normalizeCacheUrl = (url = "") => {
  if (!url) return url;
  if (_.includes(url, "/auth/me")) return url.split("?")[0];
  return url;
};

export const cacheSetUrl = async (method, url, data) => {
  if (!url) return;
  await forage.setItem(urlKey(method, normalizeCacheUrl(url)), { data, cachedAt: Date.now() });
};

export const cacheGetUrl = async (method, url) => {
  return forage.getItem(urlKey(method, normalizeCacheUrl(url)));
};

export const cacheSetEntity = async (slice, entity) => {
  const id = _.get(entity, "id");
  if (!slice || id == null) return;
  await forage.setItem(entityKey(slice, id), entity);
  const index = (await forage.getItem(sliceIndexKey(slice))) || [];
  if (!_.includes(index, id) && !_.includes(index, String(id))) {
    await forage.setItem(sliceIndexKey(slice), [...index, id]);
  }
};

export const cacheGetEntity = async (slice, id) => {
  return forage.getItem(entityKey(slice, id));
};

export const cacheGetSlice = async (slice) => {
  const index = (await forage.getItem(sliceIndexKey(slice))) || [];
  const entities = await Promise.all(_.map(index, (id) => forage.getItem(entityKey(slice, id))));
  return _.compact(entities);
};

export const cacheRemoveEntity = async (slice, id) => {
  await forage.removeItem(entityKey(slice, id));
  const index = (await forage.getItem(sliceIndexKey(slice))) || [];
  await forage.setItem(
    sliceIndexKey(slice),
    _.filter(index, (x) => String(x) !== String(id))
  );
};

export const cacheSetCurrentUser = async (user) => {
  if (_.isEmpty(user)) return;
  await forage.setItem(CURRENT_USER_KEY, user);
};

export const cacheGetCurrentUser = async () => {
  return forage.getItem(CURRENT_USER_KEY);
};

export const cacheClearCurrentUser = async () => {
  await forage.removeItem(CURRENT_USER_KEY);
};

export const cacheClearAll = () => forage.clear();

export const cacheSetLookupsSyncedAt = async (ts) => {
  await forage.setItem(LOOKUPS_SYNCED_AT_KEY, ts);
};

export const cacheGetLookupsSyncedAt = async () => {
  return forage.getItem(LOOKUPS_SYNCED_AT_KEY);
};

// Replace a slice index so deleted lookups drop out of IndexedDB on sync.
export const cacheReplaceSlice = async (slice, entities) => {
  const prev = (await forage.getItem(sliceIndexKey(slice))) || [];
  const nextIds = _.compact(_.map(entities, "id"));
  const keep = new Set(_.map(nextIds, String));
  await Promise.all(
    _.map(prev, (id) => {
      if (keep.has(String(id))) return null;
      return forage.removeItem(entityKey(slice, id));
    })
  );
  await Promise.all(
    _.map(entities, (entity) => {
      const id = _.get(entity, "id");
      if (id == null) return null;
      return forage.setItem(entityKey(slice, id), entity);
    })
  );
  await forage.setItem(sliceIndexKey(slice), nextIds);
};

// Lookup tables must match before parent record paths (statuses live under /action_items, etc.)
const LOOKUP_PATH_SLICES = [
  ["/projects/all_areas", "project_areas"],
  ["/technical_details/paint_systems", "project_technical_details_paint_systems"],
  ["/moneyworks/jobs", "mw_jobs"],
  ["/moneyworks/products", "mw_products"],
  ["/action_items/statuses", "project_qa_action_items_statuses"],
  ["/welding_inspections/welding_types", "project_qa_welding_inspections_welding_types"],
  ["/welding_inspections/welding_processes", "project_qa_welding_inspections_welding_processes"],
  ["/welding_inspections/welding_stages", "project_qa_welding_inspections_welding_stages"],
  ["/welding_inspections/welding_standards", "project_qa_welding_inspections_welding_standards"],
  ["/welding_inspections/welding_procedures", "project_qa_welding_inspections_welding_procedures"],
  ["/welding_inspections/access_restrictions", "project_qa_welding_inspections_access_restrictions"],
  ["/welding_inspections/viewing_conditions", "project_qa_welding_inspections_viewing_conditions"],
  ["/welding_inspections/surface_conditions", "project_qa_welding_inspections_surface_conditions"],
  ["/welding_inspections/fillet_sizes", "project_qa_welding_inspections_fillet_sizes"],
  ["/welding_inspections/inspection_specs", "project_qa_welding_inspections_inspection_specs"],
  ["/welding_inspections/inspection_criteria", "project_qa_welding_inspections_inspection_criterias"],
  ["/welding_inspections/failure_reasons", "project_qa_welding_inspections_failure_reasons"],
  ["/store/overalls/statuses", "store_overalls_statuses"],
  ["/store/overalls/garments", "store_overalls_garments"],
  ["/paint/locations", "paint_locations"],
  ["/workshops", "workshops"],
  ["/address_book/companies", "address_book_companies"],
];

// Guess Redux slice from common mobile API URL patterns
export const guessSliceFromUrl = (url = "") => {
  const path = url.split("?")[0];
  const lookup = _.find(LOOKUP_PATH_SLICES, ([needle]) => _.includes(path, needle));
  if (lookup) return lookup[1];

  if (_.includes(path, "/custom_forms/") && _.includes(path, "/records")) return "custom_form_records";
  if (_.includes(path, "/qa/action_items")) return "project_qa_action_items";
  if (_.includes(path, "/qa/paint_inspections")) return "project_qa_paint_inspections";
  if (_.includes(path, "/qa/welding_inspections")) return "project_qa_welding_inspections";
  if (_.includes(path, "/site_installations")) return "project_qa_site_site_installations";
  if (_.includes(path, "/surveillances")) return "project_qa_site_surveillances";
  if (_.includes(path, "/drawings")) return "project_drawings";
  if (_.includes(path, "/store/bookout_items")) return "store_bookout_items";
  if (_.includes(path, "/store/overalls")) return "store_overalls";
  if (_.includes(path, "/store/requests")) return "store_requests";
  if (_.includes(path, "/approvals") && !_.includes(path, "/steps")) return "approvals";
  if (_.includes(path, "/approval/steps")) return "approval_steps";
  if (_.includes(path, "/custom_forms/menus")) return "custom_forms_menus";
  if (_.includes(path, "/custom_forms")) return "custom_forms";
  if (_.includes(path, "/employees")) return "employees";
  if (_.includes(path, "/user_managements")) return "users";
  if (_.includes(path, "/areas")) return "project_areas";
  if (_.includes(path, "/projects") || path === "/projects.json") return "projects";
  if (_.includes(path, "/auth/me") || _.includes(path, "/current_project")) return "current_user";
  return null;
};

export const writeThroughResponse = async (method, url, data) => {
  await cacheSetUrl(method, url, data);
  const slice = guessSliceFromUrl(url);
  if (!slice) return;

  if (slice === "current_user") {
    await cacheSetCurrentUser(data);
    return;
  }

  if (_.isArray(data)) {
    await Promise.all(_.map(data, (item) => cacheSetEntity(slice, item)));
  } else if (_.isObject(data) && data.id != null) {
    await cacheSetEntity(slice, data);
  }
};

export default forage;
